// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — after a marker scan, a green square drawn where this device
 * believes the marker's edge is (`docs/SHARED_AR_PLAN.md`). If it sits on
 * the printed edge, the scan was good; if it floats behind the sheet or
 * is the wrong size, the scan measured the wrong surface and should be
 * done again. It is the only way a person holding the phone can see the
 * scan's error, and every phone in a room can check its own at a glance.
 *
 * Shown for a few seconds, then faded out. A flat square annulus whose
 * stroke straddles the marker's outer edge, scaled to the measured side,
 * drawn over everything (the camera picture is not in the depth buffer,
 * and the sphere above must not hide it).
 */

import type * as THREE from 'three'

export interface Vec3Like {
  x: number
  y: number
  z: number
}

export interface QuatLike extends Vec3Like {
  w: number
}

export interface VrMarkerOutlineHandle {
  readonly mesh: THREE.Mesh
  /** Show the outline of a marker `size` metres across, turned as the marker is. */
  show(size: number, orientation: QuatLike, nowMs: number): void
  /**
   * Each frame: put the outline on the marker's middle and fade it when
   * its time is up. `position` null hides it (the sphere was placed by
   * hand since).
   */
  update(position: Vec3Like | null, nowMs: number): void
  isShown(): boolean
  dispose(): void
}

/** Full strength for this long after a scan, milliseconds. */
export const OUTLINE_HOLD_MS = 8000
/** Then faded out over this long. */
export const OUTLINE_FADE_MS = 1000
/** Stroke width as a share of the marker's side, centred on the edge. */
const STROKE = 1 / 16
/** Lift off the surface, so the square is not lost in it, metres. */
const LIFT_M = 0.003
const OPACITY = 0.9
/** The app's accent green, the same as the scan button's. */
const COLOUR = 0x22c55e

/** The outline's opacity `elapsedMs` after it was shown. */
export function outlineOpacity(elapsedMs: number): number {
  if (elapsedMs < 0) return 0
  if (elapsedMs <= OUTLINE_HOLD_MS) return OPACITY
  const fade = (elapsedMs - OUTLINE_HOLD_MS) / OUTLINE_FADE_MS
  return fade >= 1 ? 0 : OPACITY * (1 - fade)
}

export function createVrMarkerOutline(THREE_: typeof THREE): VrMarkerOutlineHandle {
  // A ring of four segments started at 45° is a square annulus with
  // axis-aligned sides; its radii are half-diagonals. One unit across,
  // laid flat (+Y up, as the marker's frame).
  const geometry = new THREE_.RingGeometry(
    (0.5 - STROKE / 2) * Math.SQRT2,
    (0.5 + STROKE / 2) * Math.SQRT2,
    4,
    1,
    Math.PI / 4,
  )
  geometry.rotateX(-Math.PI / 2)
  const material = new THREE_.MeshBasicMaterial({
    color: COLOUR,
    transparent: true,
    opacity: 0,
    depthTest: false,
    depthWrite: false,
    side: THREE_.DoubleSide,
  })
  const mesh = new THREE_.Mesh(geometry, material)
  mesh.renderOrder = 1000
  mesh.visible = false
  mesh.name = 'marker-outline'

  let shownAt: number | null = null

  function hide(): void {
    shownAt = null
    mesh.visible = false
    material.opacity = 0
  }

  return {
    mesh,
    show(size, orientation, nowMs) {
      shownAt = nowMs
      mesh.scale.setScalar(size)
      mesh.quaternion.set(orientation.x, orientation.y, orientation.z, orientation.w)
      material.opacity = OPACITY
      // Placed by the first update, so it never shows at the origin.
      mesh.visible = false
    },
    update(position, nowMs) {
      if (shownAt === null) return
      const opacity = outlineOpacity(nowMs - shownAt)
      if (!position || opacity <= 0) {
        hide()
        return
      }
      mesh.position.set(position.x, position.y + LIFT_M, position.z)
      material.opacity = opacity
      mesh.visible = true
    },
    isShown: () => shownAt !== null,
    dispose() {
      hide()
      geometry.dispose()
      material.dispose()
    },
  }
}
